import { AnyFMColumn, Column, FMColumn } from './model';

import { canAddRemoveNodes } from '../sharing/selectors';
import { createSelector } from '@reduxjs/toolkit';
import { CHNodeIsFolder } from '../../../general/node';
import { RootSelector } from '../../models/rootSelector';
import { Page } from '../../models/page';
import { getPage, getSearchParams } from '../app/selectors';

import { getRelativeLocation, RelativeLocationDescriptor } from '../breadcrumbs/selectors';

import { getColumnToggles, getNodes } from './selectors/selectors';
import * as btcolumns from './baseTableColumns';
import { secondaryColumns, SecondaryColumn } from './secondaryColumns';
import { sum } from 'lodash';
import { nonNull } from '../../../general/utils/nonNull';

export const columnIsAvailableHere = (sc: SecondaryColumn, rl: RelativeLocationDescriptor) => {
  if (sc.showOnLevels === true) return true;
  return sc.showOnLevels(rl);
};

/**
 * Выдача AI-поиска (MYUA-16771): бэкенд отдаёт только успешно размеченные креативы и не
 * возвращает в них `plot_recognition_status`, поэтому столбец `AI Metadata` там был бы
 * пустым и бессмысленным — ни в таблице, ни в списке выбора колонок его быть не должно.
 */
export const getIsAiSearch: RootSelector<boolean> = createSelector(
  [getPage, getSearchParams],
  (page, searchParams) => page === Page.SEARCH && (searchParams?.ai ?? false),
);

export const columnHiddenOnAiSearch = (sc: SecondaryColumn) => sc.key === Column.recognition;

export const getColumns: RootSelector<FMColumn<any>[]> = createSelector(
  [canAddRemoveNodes, getPage, getRelativeLocation, getNodes, getColumnToggles, getIsAiSearch],
  (canAddRemoveNodes, page, relativeLocation, nodes, columnToggles, isAiSearch) => {
    const selector = canAddRemoveNodes ? btcolumns.shareSelectorCol : btcolumns.selectorCol;
    const isSearch = page === Page.SEARCH;
    const allFolders = nodes.every(CHNodeIsFolder);
    const isSharedFolder = page === Page.SHARED_FOLDER;

    const inTypeInProject = relativeLocation.insideType || relativeLocation.onTypeLevel;
    const optional = secondaryColumns
      // разрешаем только essential, выбранные и только вне
      .filter((optionalColumn) => {
        const setting = columnToggles[optionalColumn.key] ?? true;
        return optionalColumn.essential || setting || !inTypeInProject;
      })

      // разрешаем только те, которые разрешены по правилам showOnLevels
      .filter((optionalColumn) => columnIsAvailableHere(optionalColumn, relativeLocation))

      // не показываем size, если все на этом уровне - папки
      .filter((optionalColumn) => !(optionalColumn.key === Column.size && allFolders))

      // не показываем tags на шаред папках
      .filter((optionalColumn) => !(optionalColumn.key === Column.tags && isSharedFolder))

      // не показываем AI Metadata в выдаче AI-поиска
      .filter((optionalColumn) => !(isAiSearch && columnHiddenOnAiSearch(optionalColumn)))
      .map((x) => x.column);

    const columns: (AnyFMColumn | null)[] = [
      selector,
      btcolumns.nameCol,
      isSearch && relativeLocation.onTypeLevel ? btcolumns.creativeNameCol : null,
      isSearch ? btcolumns.breadcrumbsCol : null,
      ...optional,
    ];
    return columns.filter(nonNull);
  },
);

export const getTotalWidth = createSelector(getColumns, (cols) => sum(cols.map((x) => x.width)));
